import { Textarea } from "../Textarea"
import basket from "../../../assets/fonts/deleteBasket.svg"
import { FC, useRef, useState } from "react"
import { FormikType } from "../../../types/VisitReports/types"
import "./styles.scss"
import { PlaceAutocompleteInput, LegacyPlace } from "../../base/PlaceAutocompleteInput"
import { ManualAddressSection } from "../../base/ManualAddressSection"
import { manualCoordinateInlineErrors } from "../../../validationSchemas/manualAddressRules"
import { Input } from '../../base/Input';
import {FormErrorMessage} from '../../base/FormErrorMessage';
import { useTranslation } from "../../../hooks/useTranslation"
import { useSelector } from "react-redux"
import { deriveManualDefaults } from "../../../utils/departureDefaults"
import { TextOnlyAddressConfirmDialog } from "../../base/TextOnlyAddressConfirmDialog"
import { TextOnlyAddressBadge } from "../../base/TextOnlyAddressBadge"

type Props = {
  formik: FormikType
  personIndex: number
  addressIndex: number
  path: "updated_persons" | "new_persons"
  handleAddress: (e: any, addressIndex: number) => void
  handleCoordinates: (e: any, addressIndex: number) => void
  handleAddressNote: (e: any, addressIndex: number) => void
}

export const Address: FC<Props> = ({
  formik,
  personIndex,
  addressIndex,
  path,
  handleAddress,
  handleCoordinates,
  handleAddressNote,
}) => {
  const { p } = useTranslation('contact_info');
  const workspace = useSelector((state: any) => state.authStore?.workspace);
  const { state: defaultState, country: defaultCountry } = deriveManualDefaults(workspace);
  const addressValue = formik.values?.[path]?.[personIndex]?.addresses[addressIndex] || {};
  const isManual = !!(addressValue as any)?.is_manual;
  const isTextOnly = !!(addressValue as any)?.is_text_only;
  const [pendingTextOnly, setPendingTextOnly] = useState<string | null>(null);
  const setAddressField = (leaf: string, value: unknown) =>
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}].${leaf}`, value);

  const confirmTextOnly = () => {
    if (pendingTextOnly === null) return;
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}]`, {
      ...addressValue,
      is_text_only: true,
      is_manual: false,
      formatted_address: pendingTextOnly,
      fullAddress: pendingTextOnly,
      place_id: "", latitude: "", longitude: "",
      country: "", state: "", city: "", street_name: "", zip_code: "",
      apartment_number: "", unit_number: "",
      routing_latitude: "", routing_longitude: "",
    });
    setPendingTextOnly(null);
  };
  // Coordinate errors for a MANUAL address, surfaced only after a submit attempt.
  const addressErrors =
    formik.submitCount > 0
      ? (formik?.errors as any)?.[path]?.[personIndex]?.addresses?.[addressIndex]
      : undefined;
  // An out-of-range coordinate shows LIVE (unambiguous wrong value); the submit-gated schema
  // error covers "missing". Live takes precedence.
  const liveCoordErrors = manualCoordinateInlineErrors(addressValue);
  // Tracks whether the next onChange was triggered by a Google Places selection.
  // When the user picks a suggestion, the Autocomplete widget updates the input
  // value (firing onChange) AFTER onPlaceChanged has already set the coordinates.
  // Without this guard, the error condition would immediately re-evaluate while
  // React is still batching the place_id setFieldValue update, causing a false
  // "Please select an address from the suggestions" error on first selection.
  const isSelectingFromAutocomplete = useRef(false)

  const handlePlaceSelected = (place: LegacyPlace) => {
    // Raise the flag before setting coordinates so that the onChange fired
    // by the input filling does not trigger the error condition while React is
    // still batching the place_id update.
    isSelectingFromAutocomplete.current = true
    // handleCoordinates already writes fullAddress as part of its single merged
    // setFieldValue call — no separate/redundant write needed here.
    handleCoordinates(place, addressIndex)
    // Lower the flag after all synchronous work is done so that subsequent
    // manual keystrokes are handled normally.
    setTimeout(() => {
      isSelectingFromAutocomplete.current = false
    }, 0)
  }

  const getAddress = (e: any) => {
    // If onChange was fired as a side-effect of the autocomplete selection,
    // skip the update — fullAddress and place_id are already being set by
    // onPlaceChanged / handleCoordinates.
    if (isSelectingFromAutocomplete.current) {
      return
    }
    handleAddress(e, addressIndex)
    // Editing the text exits a prior "save as text only" choice too, so it resumes the
    // normal Google-suggestions flow instead of silently staying flagged text-only.
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}].is_text_only`, false)
  }

  const getAddressNote = (e: any) => {
    handleAddressNote(e, addressIndex)
  }

  const handleDeleteAddress = () => {
    if (formik.values?.[path]) {
      formik.values?.[path]?.[personIndex].addresses.splice(addressIndex, 1)
      formik.setFieldValue(`${path}[${personIndex}].addresses`, [
        ...(formik.values?.[path]?.[personIndex].addresses || []),
      ])
    }
  }

  return (
    <div className="visit-report-address">
      <div className="visit-report-address__header">
        <div className="visit-report-address__header-container">
          <div className="visit-report-address__title-container">
            { p('work_address') }
            <img
              src={basket}
              alt={basket}
              className="visit-report-address__img"
              onClick={handleDeleteAddress}
            />
          </div>
        </div>
      </div>
      <div className="visit-report-address__container">
        <div className="visit-report-address__address">
          {!isManual && (
            <div className="visit-report-address__autocomplete">
              <PlaceAutocompleteInput
                label={p('address')}
                placeholder={p('write_address')}
                value={
                  formik.values?.[path]?.[personIndex]?.addresses[addressIndex]
                    ?.fullAddress || ""
                }
                includedPrimaryTypes={['street_address', 'premise', 'subpremise']}
                onChange={getAddress}
                onPlaceSelected={handlePlaceSelected}
                textOnlyLabel={p('save_as_text_only')}
                onSaveAsTextOnly={(text) => setPendingTextOnly(text)}
              />
              {isTextOnly && (
                <div style={{ display: "flex", justifyContent: "flex-start", marginTop: 4 }}>
                  <TextOnlyAddressBadge isTextOnly />
                </div>
              )}
            </div>
          )}
          {!isManual && !isTextOnly &&
            (((formik.touched[path] as { [key: string]: boolean }[])?.[personIndex] as { [key: string]: boolean })?.addresses as unknown as { [key: string]: boolean }[])?.[addressIndex]?.fullAddress &&
            !!formik.values?.[path]?.[personIndex]?.addresses[addressIndex]?.fullAddress &&
            !formik.values?.[path]?.[personIndex]?.addresses[addressIndex]?.place_id && (
              <FormErrorMessage message={p('select_address_from_suggestions')} />
            )
          }
          <TextOnlyAddressConfirmDialog
            open={pendingTextOnly !== null}
            addressText={pendingTextOnly || ''}
            onConfirm={confirmTextOnly}
            onCancel={() => setPendingTextOnly(null)}
          />
        </div>
        <div className="visit-report-address__apartment">
          <Input
            label={p('apartment_number')}
            placeholder={p('apartment_number')}
            value={
              formik.values?.[path]?.[personIndex]?.addresses[addressIndex]
                ?.apartment_number || ''
            }
            onChange={(e: any) => {
              formik.setFieldValue(
                `${path}[${personIndex}].addresses[${addressIndex}].apartment_number`,
                e.target.value
              )
            }}
          />
        </div>
        {/* Manual-address toggle + (when on) editable text/coordinates/routing point. */}
        <ManualAddressSection
          value={addressValue as any}
          setField={setAddressField}
          p={p}
          defaultCountry={defaultCountry}
          defaultState={defaultState}
          latitudeError={liveCoordErrors.latitude || (addressErrors as any)?.latitude}
          longitudeError={liveCoordErrors.longitude || (addressErrors as any)?.longitude}
        />

        <div className="visit-report-address__textarea">
          <Textarea
            label={p('address_note')}
            placeholder={p('write_some_note')}
            value={
              formik.values?.[path]?.[personIndex]?.addresses[addressIndex]
                ?.info_note
            }
            onChange={getAddressNote}
          />
        </div>
      </div>
    </div>
  )
}
